import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/candidate-dashboard/ui/card";
import { Progress } from "../../components/candidate-dashboard/ui/progress";
import { VideoPlayer } from "../../components/candidate-dashboard/training/video-player";
import { PlayCircle, PauseCircle, CheckCircle } from "lucide-react";
import { Button } from "../../components/candidate-dashboard/ui/button";
import tokenService from "../../services/token-service";
import apiUrls from "../../config/api-urls";
import { useNavigate } from "react-router-dom";

export default function Training() {
  const [trainingVideos, setTrainingVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [completedVideos, setCompletedVideos] = useState([]);
  const [progress, setProgress] = useState(0);
  const token = tokenService.getToken();
  const navigate = useNavigate();

  useEffect(() => {
    //  const savedProgress = localStorage.getItem("trainingProgress");
    // const savedCompleted = localStorage.getItem("completedVideos");

    // if (savedProgress) setProgress(Number(savedProgress));
    // if (savedCompleted) setCompletedVideos(JSON.parse(savedCompleted));


    const fetchTrainingVideos = async () => {
      try {
        const jobId = localStorage.getItem("jobId");
        const response = await axios.get(apiUrls.tests.training, {
          headers: { Authorization: `Bearer ${token}` },
          params: { jobId },
        });

        if (response.data.success && Array.isArray(response.data.data)) {
          const fetchedVideos = response.data.data;
          setTrainingVideos(fetchedVideos);

          // Auto-select first video
          if (fetchedVideos.length > 0) {
            const firstVideo = fetchedVideos[0];
            setCurrentVideo({
              url: firstVideo.videoUrl,
              title: firstVideo.title,
              description: firstVideo.description || "",
              trainingId: firstVideo._id,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching training videos:", error);
      }
    };

    fetchTrainingVideos();
  }, [token]);

  const markVideoCompleted = async (trainingId) => {
    try {
      const jobId = localStorage.getItem("jobId");
      const response = await axios.post(
        apiUrls.training.markCompleted,
        { jobId, trainingId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { training } = response.data;
        // Update completed videos and progress from backend
        setCompletedVideos(training.completedVideos || []);
        setProgress(training.progress || 0);

        localStorage.setItem("trainingProgress", training.progress || 0);
        localStorage.setItem(
          "completedVideos",
          JSON.stringify(training.completedVideos || [])
        );

        if (training.completed) {
          
          setTimeout(() => {
            navigate("/dashboard", { replace: true });
            window.location.reload();
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error marking video as completed:", error);
    }
  };

  const handleVideoClick = (video) => {
    if (currentVideo?.url === video.videoUrl) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentVideo({
        url: video.videoUrl,
        title: video.title,
        description: video.description || "",
        trainingId: video._id,
      });
      setIsPlaying(true);
    }
  };

  return (
    <>
      <h2 className="text-2xl font-bold mb-5 text-gray-900 dark:text-gray-100">Welcome to the Training</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Video Player Section */}
        <div className="lg:col-span-2">
          {currentVideo && (
            <VideoPlayer
              url={currentVideo.url}
              title={currentVideo.title}
              isPlaying={isPlaying}
              onPlayPause={setIsPlaying}
              trainingId={currentVideo.trainingId}
              onEnd={() => markVideoCompleted(currentVideo.trainingId)}
            />
          )}

          <Card className="mt-6 bg-white dark:bg-slate-800">
            <CardHeader>
              <CardTitle>Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc pl-5 space-y-2">
                <li>Watch each video carefully and take notes.</li>
                <li>Complete exercises or assignments mentioned.</li>
                <li>
                  Videos will automatically mark as completed when finished.
                </li>
                <li>
                  Once all videos are done, training will be marked as complete.
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Video List Section */}
          <Card className="lg:col-span-1 bg-white dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="mb-4">
              <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-100">Course Progress</h3>
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground mt-1 text-gray-600 dark:text-gray-300">
                {completedVideos.length} of {trainingVideos.length} videos
                completed ({Math.round(progress)}%)
              </p>
            </div>

            <div className="space-y-2">
                <h3 className="font-semibold mb-3 text-gray-800 dark:text-gray-100">Training Videos</h3>
              {trainingVideos.map((video) => {
                const isCurrent = currentVideo?.url === video.videoUrl;
                const isCompleted = completedVideos.includes(video._id);

                return (
                  <div
                    key={video._id}
                    onClick={() => handleVideoClick(video)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors cursor-pointer text-gray-800 dark:text-gray-100 ${
                      isCurrent ? "bg-accent" : ""
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <div className="h-5 w-5 border-2 rounded-full border-muted-foreground" />
                    )}

                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900 dark:text-gray-50">
                          Video {video.order}: {video.title}
                        </p>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVideoClick(video);
                      }}
                    >
                      {isCurrent && isPlaying ? (
                        <PauseCircle className="h-4 w-4" />
                      ) : (
                        <PlayCircle className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                );
              })}

              {trainingVideos.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No training videos available yet.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
